import camelcaseKeys from 'camelcase-keys';
import { camelCase } from 'lodash-es';

import { type AppAsset } from 'api/types/AppAsset';
import { type RawAppAsset } from 'api/types/AppAsset/RawAppAsset';

import { convertImgixUrl } from 'helpers/convertImgixUrl';

const isNonNullableKey = <K extends keyof AppAsset, A extends (keyof AppAsset)[]>(
  array: A,
  key: K,
): key is Extract<K, A[number]> => {
  return array.includes(key);
};

const parseType = (type: RawAppAsset['type']) => {
  switch (type) {
    case 'videolink':
    case 'iframe-link':
      return camelCase(type).toLowerCase().replace('link', '') as AppAsset['type'];

    case 'internal-link':
      return 'internal';

    default:
      return camelCase(type) as AppAsset['type'];
  }
};

export const parseAssetData = (rawData: RawAppAsset) => {
  const data = camelcaseKeys({ ...rawData }, { deep: true });

  const contactUs = data?.contactUs
    ?.filter((contact) => {
      return contact.id !== undefined;
    })
    .map((contact) => {
      return camelcaseKeys({ ...contact });
    })
    ?.map((contact) => {
      const emails = (
        contact.emails.filter((item) => {
          return item.email !== undefined;
        }) as { email: string }[]
      ).map((item) => {
        return item.email;
      });

      return {
        ...contact,
        emails,
      };
    });

  const parsedData: AppAsset = {
    id: data.id?.toString(),
    appId: data.appId?.toString(),
    type: parseType(data.type),
    title: data.title,
    subtitle: data.subtitle || null,
    image: convertImgixUrl(data.image),
    url: data.url || null,
    thumbnail: data.thumbnail ? convertImgixUrl(data.thumbnail) : null,
    shareable: Boolean(data.shareable),
    fullCompleteUnlockOnly: Boolean(data.fullCompleteUnlockOnly),
    graduatedUnlockOnly: Boolean(data.graduatedUnlockOnly),
    inDevelopment: Boolean(data.inDevelopment),
    regionType: data.regionType || null,
    state: data.state || null,
    region: data.region || null,
    tiers: [data.tier1 || 0, data.tier2 || 0, data.tier3 || 0, data.tier4 || 0],
    countries: data.countries || null,
    itemId: data.itemId,
    contactUs: contactUs || [],
  };

  Object.keys(parsedData).forEach((k) => {
    const key = k as keyof AppAsset;
    const value = parsedData[key];

    const isNullableString = isNonNullableKey(['subtitle', 'url', 'thumbnail', 'countries'], key);

    if (typeof value === 'string' && value.length === 0 && isNullableString) {
      parsedData[key] = null;
      return;
    }

    const isNumericBoolean = isNonNullableKey(['shareable', 'graduatedUnlockOnly', 'inDevelopment'], key);

    if (typeof value === 'number' && [0, 1].includes(value) && isNumericBoolean) {
      parsedData[key] = Boolean(value);
    }
  });

  parsedData.id = data.id.toString();
  parsedData.appId = data.appId.toString();

  if (data.image) {
    parsedData.image = convertImgixUrl(data.image);
  }

  if (data.thumbnail) {
    parsedData.thumbnail = convertImgixUrl(data.thumbnail);
  }

  if (data.type === 'pdf') {
    parsedData.url = `https://stack.travpromobile.com/viewer.php?file=${data?.url}#zoom=page-fit`;
  }

  return parsedData;
};
